/**
 * The route a push notification opens, derived from its payload.
 *
 * Deliberately pure and Capacitor-free so it can be exercised in jsdom: this is the
 * one piece of the notification path a browser test can reach at all.
 *
 * The server does send a `route` key (`api/api/services/push.py`), and it is ignored
 * here. A notification payload is attacker-shaped input — anything that can mint an
 * FCM message for this project decides where the member lands — so the destination is
 * *derived* from `type` plus an identifier that is validated against the same slug
 * grammar the API mints, never read out of a free-form string.
 */

/** Exactly the slugs the backend generates: lowercase words joined by single hyphens. */
const EVENT_SLUG = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/** Team chat is a dialog on the profile screen, opened by a query parameter. */
const CHAT_ROUTE = "/profile?chat=open";

export type NotificationKind = "chat_message" | "event_reminder";

const KINDS: readonly NotificationKind[] = ["chat_message", "event_reminder"];

function stringField(data: unknown, key: string): string | null {
  if (typeof data !== "object" || data === null) return null;
  const value = (data as Record<string, unknown>)[key];
  // FCM data payloads are string-to-string on the wire, so anything else is either a
  // malformed sender or a platform quirk; both are safest treated as absent.
  return typeof value === "string" ? value : null;
}

/** The notification's kind, or null for a payload this build does not handle. */
export function notificationKind(data: unknown): NotificationKind | null {
  const type = stringField(data, "type");
  return KINDS.find((kind) => kind === type) ?? null;
}

/** Where a tap on this notification should go, or null when nowhere is safe. */
export function routeFromNotificationData(data: unknown): string | null {
  switch (notificationKind(data)) {
    case "chat_message":
      return CHAT_ROUTE;
    case "event_reminder": {
      const slug = stringField(data, "event_slug");
      return slug && EVENT_SLUG.test(slug) ? `/events/${slug}` : null;
    }
    default:
      return null;
  }
}
